import { gql, useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { animated, useSpring } from "react-spring";
import ChipsLeaderboardWidget from "./ChipsLeaderBoard";
import Error from "./Error";
import Loading from "./Loading";

const GET_TIPPER_CHIPS = gql`
  query GetTipperChips($username: String!, $broadcaster: String!) {
    getTipperChips(username: $username, broadcaster: $broadcaster)
  }
`;

interface TierConfig {
  gradient: string;
  glowColor: string;
  animation: string;
  borderGradient: string;
  particleEffect: string;
}

const getTierConfig = (chips: number): TierConfig => {
  if (chips >= 1000) {
    return {
      gradient: "from-purple-400 via-pink-500 to-red-500",
      glowColor:
        "shadow-[0_0_30px_rgba(168,85,247,0.8),0_0_60px_rgba(236,72,153,0.6)]",
      animation: "animate-pulse",
      borderGradient:
        "border-4 border-transparent bg-gradient-to-r from-purple-500 via-pink-500 to-red-500",
      particleEffect: "✨",
    };
  } else if (chips >= 500) {
    return {
      gradient: "from-yellow-300 via-orange-400 to-red-500",
      glowColor:
        "shadow-[0_0_25px_rgba(251,191,36,0.7),0_0_50px_rgba(249,115,22,0.5)]",
      animation: "animate-bounce",
      borderGradient:
        "border-4 border-transparent bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500",
      particleEffect: "🔥",
    };
  } else if (chips >= 250) {
    return {
      gradient: "from-blue-400 via-cyan-400 to-teal-400",
      glowColor:
        "shadow-[0_0_20px_rgba(96,165,250,0.6),0_0_40px_rgba(34,211,238,0.4)]",
      animation: "",
      borderGradient:
        "border-4 border-transparent bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400",
      particleEffect: "⭐",
    };
  } else if (chips >= 100) {
    return {
      gradient: "from-green-400 via-emerald-400 to-teal-500",
      glowColor: "shadow-[0_0_15px_rgba(74,222,128,0.5)]",
      animation: "",
      borderGradient: "border-2 border-green-400",
      particleEffect: "💎",
    };
  } else {
    return {
      gradient: "from-gray-400 via-gray-500 to-gray-600",
      glowColor: "",
      animation: "",
      borderGradient: "border-2 border-gray-500",
      particleEffect: "🎲",
    };
  }
};

const TipperChipsWidget = () => {
  const { broadcaster, username } = useParams<{
    broadcaster: string;
    username: string;
  }>();

  const { loading, error, data } = useQuery(GET_TIPPER_CHIPS, {
    variables: { username, broadcaster },
    pollInterval: 5000, // Refresh every 10 seconds
    skip: !username || !broadcaster,
  });

  const chips = data?.getTipperChips ?? 0;
  const raffleTickets = Math.ceil(chips / 200);
  const tierConfig = getTierConfig(chips);

  const { chipsVal } = useSpring({
    chipsVal: chips,
    from: { chipsVal: 0 },
    config: {
      mass: 20,
      friction: 40,
      tension: 20,
    },
  });

  const { ticketsVal } = useSpring({
    ticketsVal: raffleTickets,
    from: { ticketsVal: 0 },
    config: {
      mass: 20,
      friction: 40,
      tension: 20,
    },
  });

  if (!username || !broadcaster) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-transparent">
        <div className="text-white text-2xl">
          Invalid URL: Missing username or broadcaster
        </div>
      </div>
    );
  }

  if (loading) return <Loading />;
  if (error) return <Error error={error} />;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-transparent gap-8 p-4">
      {/* Individual Chips Widget */}
      <div
        className={`text-center p-8 rounded-xl ${tierConfig.borderGradient} ${tierConfig.glowColor} bg-black bg-opacity-80 backdrop-blur-sm`}
      >
        <div className="text-white text-4xl font-bold mb-4 flex items-center justify-center gap-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          <span>{tierConfig.particleEffect}</span>
          <span>{username}</span>
          <span>{tierConfig.particleEffect}</span>
        </div>
        <div
          className={`text-8xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r ${tierConfig.gradient} ${tierConfig.animation} mb-4 drop-shadow-[0_4px_8px_rgba(0,0,0,0.9)]`}
          style={{
            filter: "drop-shadow(0 0 10px rgba(0, 0, 0, 0.9))",
          }}
        >
          <animated.span>
            {chipsVal.to((val) => Math.floor(val).toLocaleString())}
          </animated.span>
        </div>
        <div className="text-white text-3xl font-semibold mb-4 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          Chips
        </div>

        <div className="divider my-4"></div>

        <div
          className={`text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${tierConfig.gradient} drop-shadow-[0_4px_8px_rgba(0,0,0,0.9)]`}
          style={{
            filter: "drop-shadow(0 0 8px rgba(0, 0, 0, 0.9))",
          }}
        >
          <animated.span>
            {ticketsVal.to((val) => Math.floor(val).toLocaleString())}
          </animated.span>
        </div>
        <div className="text-white text-2xl mt-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          Raffle Tickets
        </div>
      </div>

      {/* Chips Leaderboard */}
      <div className="w-full max-w-2xl">
        <ChipsLeaderboardWidget broadcaster={broadcaster} />
      </div>
    </div>
  );
};

export default TipperChipsWidget;
